// Copyright (c) 2026. LodgIQ Inc, All rights reserved
//
// Scratch calculator for Ask LodgIQ — the hidden one. Rendered under an
// assistant message when the intent gate sees "give me a calculator"
// (chat/intents.py); it is NOT a tool, so the model can neither advertise nor
// invoke it, and it never appears in the capability list or the prompt gallery.
//
// Everything is computed HERE, in the browser. Once the pad is open it costs
// nothing at all — no request, no tokens, however many sums the user does. That
// is the entire point of the feature, so keep it that way: no service calls.
//
// This is deliberately NOT CalculatorCard.js — that one is the RM Revenue
// Calculator (displacement, break-even, KPI solving) and posts to /calculate/.

import React, { useCallback, useEffect, useRef, useState } from "react";

const KEYS = [
  ["C", "(", ")", "÷"],
  ["7", "8", "9", "×"],
  ["4", "5", "6", "-"],
  ["1", "2", "3", "+"],
  ["0", ".", "%", "="],
];

const OPERATORS = "+-×÷*/";
const MAX_LEN = 60;          // a scratch pad, not a spreadsheet
const MAX_TAPE = 8;

/** Tokenise and evaluate with two precedence passes.
 *
 * Hand-rolled rather than eval(): eval() on user input is a habit worth not
 * having, even client-side, and this also lets % mean percent-of rather than
 * modulo, which is what people expect from a calculator keypad.
 * Returns a number, or null when the expression isn't complete/valid.
 */
function evaluate(raw) {
  const expr = String(raw || "").replace(/×/g, "*").replace(/÷/g, "/").trim();
  if (!expr || /[+\-*/.(]$/.test(expr)) return null;

  const tokens = expr.match(/(\d+\.?\d*|[+\-*/()%])/g);
  if (!tokens || tokens.join("") !== expr.replace(/\s+/g, "")) return null;

  let index = 0;

  // "50+10%" reads as "10% of 50", the calculator convention.
  const applyPercent = (value, prev, op) => {
    if (op === "+" || op === "-") return (prev * value) / 100;
    return value / 100;
  };

  const parseFactor = () => {
    const token = tokens[index];
    if (token === "(") {
      index += 1;
      const value = parseTerms();
      if (tokens[index] === ")") index += 1;
      return value;
    }
    if (token === "-") { index += 1; return -parseFactor(); }
    if (token === "+") { index += 1; return parseFactor(); }
    if (token === undefined || !/^\d/.test(token)) return NaN;
    index += 1;
    return parseFloat(token);
  };

  const parseFactors = () => {
    let value = parseFactor();
    while (index < tokens.length && (tokens[index] === "*" || tokens[index] === "/")) {
      const op = tokens[index];
      index += 1;
      let right = parseFactor();
      if (tokens[index] === "%") { index += 1; right = applyPercent(right, value, op); }
      value = op === "*" ? value * right : value / right;
    }
    return value;
  };

  const parseTerms = () => {
    let value = parseFactors();
    while (index < tokens.length && (tokens[index] === "+" || tokens[index] === "-")) {
      const op = tokens[index];
      index += 1;
      let right = parseFactors();
      if (tokens[index] === "%") { index += 1; right = applyPercent(right, value, op); }
      value = op === "+" ? value + right : value - right;
    }
    return value;
  };

  const result = parseTerms();
  if (index < tokens.length && tokens[index] !== "%") return null;
  if (!Number.isFinite(result)) return null;
  return result;
}

/** Thousands separators, at most 6 decimals, trailing zeros trimmed. */
function format(value) {
  if (value === null || value === undefined) return "";
  const rounded = Math.round(value * 1e6) / 1e6;
  if (Math.abs(rounded) >= 1e15) return rounded.toExponential(6);
  return rounded.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

export default function MathPadCard() {
  const [expr, setExpr] = useState("");
  const [tape, setTape] = useState([]);
  const [error, setError] = useState("");
  const cardRef = useRef(null);

  // Live preview of the current expression — no commit, no history entry.
  const preview = expr && !/[+\-*/.(]$/.test(expr) ? evaluate(expr) : null;

  const commit = useCallback(() => {
    const value = evaluate(expr);
    if (value === null) {
      if (expr) setError("That expression isn't complete.");
      return;
    }
    setError("");
    setTape((rows) => [{ expr, value }, ...rows].slice(0, MAX_TAPE));
    setExpr(format(value).replace(/,/g, ""));   // chain from the result
  }, [expr]);

  const press = useCallback((key) => {
    setError("");
    if (key === "C") { setExpr(""); return; }
    if (key === "=") { commit(); return; }
    setExpr((current) => {
      if (current.length >= MAX_LEN) return current;
      // Replace a trailing operator rather than stacking two.
      if (OPERATORS.includes(key) && OPERATORS.includes(current.slice(-1))) {
        return current.slice(0, -1) + key;
      }
      if (OPERATORS.includes(key) && !current && key !== "-") return current;
      return current + key;
    });
  }, [commit]);

  // Physical keyboard, but only while the pad has focus — otherwise typing in
  // the chat composer would land in the calculator.
  useEffect(() => {
    const node = cardRef.current;
    if (!node) return undefined;
    const onKey = (event) => {
      const { key } = event;
      if (/^[0-9.]$/.test(key)) { press(key); }
      else if (key === "+" || key === "-") { press(key); }
      else if (key === "*") { press("×"); }
      else if (key === "/") { press("÷"); }
      else if (key === "%" || key === "(" || key === ")") { press(key); }
      else if (key === "Enter" || key === "=") { press("="); }
      else if (key === "Backspace") { setExpr((c) => c.slice(0, -1)); }
      else if (key === "Escape") { press("C"); }
      else { return; }
      event.preventDefault();
      event.stopPropagation();
    };
    node.addEventListener("keydown", onKey);
    return () => node.removeEventListener("keydown", onKey);
  }, [press]);

  const copy = (text) => {
    try { navigator.clipboard.writeText(String(text)); } catch (err) { /* non-fatal */ }
  };

  return (
    <div className="wiz-pad" ref={cardRef} tabIndex={0} role="group"
      aria-label="Scratch calculator">
      <div className="wiz-pad__head">
        <span className="wiz-pad__title">Calculator</span>
        <span className="wiz-pad__note">Runs in your browser</span>
      </div>

      <div className="wiz-pad__screen">
        <div className="wiz-pad__expr">{expr || "0"}</div>
        <div className="wiz-pad__preview">
          {error || (preview !== null && expr ? `= ${format(preview)}` : "")}
        </div>
      </div>

      <div className="wiz-pad__keys">
        {KEYS.map((row, ri) => (
          <div className="wiz-pad__row" key={ri}>
            {row.map((key) => (
              <button
                key={key}
                type="button"
                className={`wiz-pad__key${OPERATORS.includes(key) || key === "%" ? " is-op" : ""}`
                  + (key === "=" ? " is-eq" : "") + (key === "C" ? " is-clear" : "")}
                onClick={() => press(key)}
                aria-label={key === "÷" ? "divide" : key === "×" ? "multiply" : key}
              >
                {key}
              </button>
            ))}
          </div>
        ))}
      </div>

      {tape.length > 0 && (
        <div className="wiz-pad__tape">
          {tape.map((row, i) => (
            <button key={i} type="button" className="wiz-pad__taperow"
              title="Copy this result" onClick={() => copy(format(row.value))}>
              <span className="wiz-pad__tapeexpr">{row.expr}</span>
              <span className="wiz-pad__tapeval">= {format(row.value)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
